import { getApi, postApi, putApi } from "../../../helpers/auth_helpers";

const base = () => String(process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "");

export type PhotoReason = "no_photo" | "dyed_now" | "colour_changed" | "poor_quality" | "other";
export type PhotoUpdateStatus = "none" | "requested" | "uploaded" | "approved" | "cleared";
export type DyeStatus = "undyed" | "in_dyeing" | "dyed";

/** The studio view of a product's photos and dye state (photoStatus on list rows, or the product endpoint). */
export type PhotoStatus = {
  dyeable: boolean;
  dyeableGuessed?: boolean;
  dyeStatus?: DyeStatus | null;
  dyedColour?: string | null;
  dyedAt?: string | null;
  needsPhoto: boolean;
  status: PhotoUpdateStatus;
  reason?: PhotoReason;
  source?: "stored" | "derived";
  note?: string | null;
  since?: string | null;
  daysOpen?: number | null;
  dueBy?: string | null;
  requestedBy?: string | null;
  alsoNoPhoto?: boolean;
  clearable?: boolean;
};

export type ApiResult<T> = { success?: boolean; data?: T; message?: string; code?: string; status?: number };

/** True when the studio has photo updates switched on (the endpoint is 404 when off). */
export const fetchPhotoUpdatesEnabled = async (): Promise<boolean> => {
  try {
    const res: ApiResult<{ enabled: boolean }> = await getApi(`${base()}/studio/photo-updates/status`);
    return Boolean(res?.success && res?.data?.enabled);
  } catch {
    return false;
  }
};

/** How many products the "Needs photo" tab would list (one-row list call, reads the total). */
export const fetchNeedsPhotoCount = async (): Promise<number> => {
  const res: any = await getApi(`${base()}/product/products?offset=0&limit=1&needsPhotoUpdate=true`);
  const n = Number(res?.data?.length);
  return Number.isFinite(n) ? n : 0;
};

const productUrl = (mongoId: string) => `${base()}/studio/products/${encodeURIComponent(mongoId)}`;

export const fetchPhotoStatus = (mongoId: string): Promise<ApiResult<PhotoStatus>> => getApi(`${productUrl(mongoId)}/photo-update`);

export const requestPhotoUpdate = (mongoId: string, body: { reason: PhotoReason; note?: string; dueBy?: string }): Promise<ApiResult<PhotoStatus>> =>
  postApi(`${productUrl(mongoId)}/photo-update`, body);

export const clearPhotoUpdate = (mongoId: string, note?: string): Promise<ApiResult<PhotoStatus>> =>
  postApi(`${productUrl(mongoId)}/photo-update/clear`, { note });

export const saveDyeStatus = (
  mongoId: string,
  body: { dyeable?: boolean; dyeStatus?: DyeStatus; dyedColour?: string; colour?: string; expectedDyeStatus?: DyeStatus | "" }
): Promise<ApiResult<PhotoStatus>> => putApi(`${productUrl(mongoId)}/dye`, body);
